import styled from '@emotion/styled/macro'
import { FlexSpinner } from 'components/FlexSpinner'
import { FieldProps } from 'formik'
import { mapFieldToDropdown } from 'formik-office-ui-fabric-react'
import { invokeAll } from 'helpers'
import { noop } from 'lodash-es'
import {
  Dropdown,
  IDropdown,
  IDropdownOption,
  IDropdownProps,
  IRenderFunction,
  ISelectableDroppableTextProps,
  ISelectableOption,
} from 'office-ui-fabric-react'
import * as React from 'react'

const DropdownListWrapper = styled.div`
  .ms-Button-flexContainer {
    flex: 1;
  }
`

export type AsyncDropdownProps = Omit<
  IDropdownProps,
  'options' | 'placeholder' | 'placeHolder'
> & {
  onResolveOptions: IDropdownOption[] | PromiseLike<IDropdownOption[]>
  placeholders?: {
    empty?: string
    loading?: string
    default?: string
  }
}

export const AsyncDropdown: React.FunctionComponent<AsyncDropdownProps> = ({
  onResolveOptions,
  onRenderOption,
  onRenderItem,
  onChange,
  placeholders,
  ...props
}) => {
  const [options, setOptions] = React.useState<IDropdownOption[]>([
    { key: 'loading', text: 'Daten werden geladen...', disabled: true },
  ])
  const isLoading = React.useMemo(
    () => options.length === 1 && options[0].key === 'loading',
    [options]
  )
  const renderOption = React.useMemo<IRenderFunction<ISelectableOption>>(
    () => (renderProps, defaultRender) =>
      renderProps && renderProps.key === 'loading' ? (
        <FlexSpinner flex="1" py={2} />
      ) : onRenderOption ? (
        onRenderOption(renderProps, defaultRender)
      ) : (
        defaultRender!(renderProps)
      ),
    [onRenderOption]
  )
  const placeholdersRef = React.useRef(placeholders)
  const placeholder = React.useMemo(() => {
    if (placeholdersRef.current) {
      const { loading, empty, default: def } = placeholdersRef.current

      if (isLoading) {
        return loading
      }
      if (options.length === 0) {
        return empty
      }
      return def
    }

    return undefined
  }, [placeholdersRef, isLoading, options.length])

  const renderList = React.useMemo<
    IRenderFunction<ISelectableDroppableTextProps<IDropdown, HTMLDivElement>>
  >(
    () => (renderProps, defaultRender) =>
      isLoading ? (
        <DropdownListWrapper>{defaultRender!(renderProps)}</DropdownListWrapper>
      ) : (
        defaultRender!(renderProps)
      ),
    [isLoading]
  )

  React.useEffect(() => {
    if (Array.isArray(onResolveOptions)) {
      setOptions(onResolveOptions)
    } else {
      onResolveOptions.then(setOptions)
    }
  }, [onResolveOptions, setOptions])

  // TODO: translate placeholder
  return (
    <Dropdown
      onRenderList={renderList}
      onRenderOption={renderOption}
      onChange={isLoading ? noop : onChange}
      options={options}
      placeholder={placeholder}
      {...props}
    />
  )
}

export type FormikAsyncDropdownProps<T> = Omit<
  AsyncDropdownProps,
  'selectedKey'
> &
  FieldProps<T>

export const FormikAsyncDropdownFactory =
  (
    asyncDropdownComponent: React.FunctionComponent<any>
  ): React.FunctionComponent<any> =>
  ({ field, form, ...props }) => {
    const AsyncDropdownComponent = asyncDropdownComponent

    const { errorMessage, onDismiss, ...fieldProps } = mapFieldToDropdown({
      field,
      form,
    })

    return (
      <AsyncDropdownComponent
        errorMessage={errorMessage}
        {...props}
        onDismiss={invokeAll<any>(onDismiss, props.onDismiss)}
        {...fieldProps}
      />
    )
  }

export const FormikAsyncDropdown = FormikAsyncDropdownFactory(AsyncDropdown)
